<template>
  <aside class="auth-showcase">
    <div class="auth-showcase__glow" aria-hidden="true"></div>

    <div class="auth-showcase__inner">
      <div class="auth-showcase__top">
        <div>
          <h2 class="auth-showcase__heading">One link for your app</h2>
          <p class="auth-showcase__lede">
            By accessing the dashboard, you are able to start for free, no
            credit card required.
          </p>
        </div>

        <div
          class="auth-showcase__slideshow"
          @mouseenter="stopAutoplay"
          @mouseleave="startAutoplay"
        >
          <transition name="auth-slide" mode="out-in">
            <div :key="activeFeature.id" class="auth-showcase__slide">
              <div class="auth-showcase__preview">
                <!-- Optimize conversion rates / Landing page / Analytics: real product screenshots, zoomed in -->
                <div
                  v-if="activeFeature.type === 'screenshot'"
                  class="device-card device-card--screenshot"
                >
                  <img
                    :src="activeFeature.screenshot"
                    :alt="activeFeature.title"
                    :style="{ objectPosition: activeFeature.focus }"
                    class="device-card__screenshot"
                  />
                </div>

                <!-- QR codes: a real 1link.io QR code -->
                <div
                  v-else-if="activeFeature.id === 'qr'"
                  class="device-card device-card--qr"
                >
                  <div class="device-card__head">
                    <v-icon size="11" color="#999">mdi-lock</v-icon>
                    <span>link-to.app/demo</span>
                  </div>
                  <img
                    src="https://1link.io/images/vendor/qr-code-example-demo.png"
                    alt="Example 1link.io QR code"
                    class="device-card__qr-img"
                  />
                  <p class="device-card__caption">Scan to install</p>
                </div>

                <!-- Custom domains: one domain, endless generated paths -->
                <div
                  v-else-if="activeFeature.id === 'domains'"
                  class="device-card device-card--domains"
                >
                  <div class="device-card__head">
                    <v-icon size="11" color="#999">mdi-lock</v-icon>
                    <span>link.example.com</span>
                  </div>
                  <ul class="device-card__list">
                    <li v-for="path in domainPaths" :key="path">{{ path }}</li>
                  </ul>
                </div>
              </div>

              <p class="auth-showcase__slide-title">
                {{ activeFeature.title }}
              </p>
              <p class="auth-showcase__slide-body">{{ activeFeature.body }}</p>
            </div>
          </transition>

          <div class="auth-showcase__dots" role="tablist">
            <button
              v-for="(feature, index) in features"
              :key="feature.id"
              type="button"
              role="tab"
              class="auth-showcase__dot"
              :class="{ 'auth-showcase__dot--active': index === activeIndex }"
              :aria-selected="index === activeIndex"
              :aria-label="'Show ' + feature.title"
              @click="goTo(index)"
            ></button>
          </div>
        </div>
      </div>

      <div class="auth-showcase__trust">
        <v-icon size="15">mdi-shield-check-outline</v-icon>
        <span>Trusted since {{ startYear }}</span>
      </div>
    </div>
  </aside>
</template>

<script>
import showcase_conversion from "../assets/showcase-conversion.png";
import showcase_landing from "../assets/showcase-landing.png";
import showcase_analytics from "../assets/showcase-analytics.png";
export default {
  name: "AuthShowcase",
  computed: {
    startYear() {
      return this.$store.state.config.APP_START_YEAR;
    },
    activeFeature() {
      return this.features[this.activeIndex];
    },
  },
  data: () => ({
    activeIndex: 0,
    // not proxied onto the instance by Vue (leading "_"); assigned as a plain property
    // eslint-disable-next-line vue/no-reserved-keys
    _timer: null,
    domainPaths: [
      "/summer-sale",
      "/black-friday",
      "/webinar-2026",
      "/app-download",
      "/promo-x92k",
      "/referral-af31",
      "/newsletter",
      "/beta-invite",
    ],
    features: [
      {
        id: "conversion",
        type: "screenshot",
        screenshot: showcase_conversion,
        focus: "center top",
        title: "Optimize conversion rates",
        body: "Your users can quickly install your app on any device, and open a specific view directly in-app.",
      },
      {
        id: "landing",
        type: "screenshot",
        screenshot: showcase_landing,
        focus: "center top",
        title: "Landing page",
        body: "1link.io automatically generates a landing page that matches your app's style for desktop devices, complete with a QR code so users can scan it and install the app.",
      },
      {
        id: "qr",
        title: "QR codes",
        body: "After creating a link, download its QR code and print it on your merch.",
      },
      {
        id: "domains",
        title: "Custom domains",
        body: "Create smart links on your custom domain, or use the free link-to.app domain.",
      },
      {
        id: "analytics",
        type: "screenshot",
        screenshot: showcase_analytics,
        focus: "left top",
        title: "Analytics",
        body: "Track performance, click origin, and which OS your users are on.",
      },
    ],
  }),
  mounted() {
    this.startAutoplay();
  },
  beforeUnmount() {
    this.stopAutoplay();
  },
  methods: {
    goTo(index) {
      this.activeIndex = index;
      this.stopAutoplay();
      this.startAutoplay();
    },
    startAutoplay() {
      this.stopAutoplay();
      if (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }
      this._timer = setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.features.length;
      }, 4500);
    },
    stopAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },
  },
};
</script>

<style scoped>
.auth-showcase {
  position: relative;
  display: none;
  overflow: hidden;
  background: #111111;
  color: rgba(255, 255, 255, 0.92);
}

.auth-showcase__glow {
  position: absolute;
  top: -20%;
  right: -15%;
  width: 60%;
  height: 60%;
  background: radial-gradient(
    circle,
    rgba(25, 118, 210, 0.35),
    transparent 70%
  );
  filter: blur(10px);
  pointer-events: none;
}

.auth-showcase__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 40px;
  height: 100%;
  padding: 40px 44px;
  overflow-y: auto;
}

.auth-showcase__top {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.auth-showcase__heading {
  margin: 0;
  max-width: 20ch;
  font-family: sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.auth-showcase__lede {
  margin: 16px 0 0;
  max-width: 42ch;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
}

/* ---------- slideshow ---------------------------------------------------- */

.auth-showcase__slideshow {
  display: flex;
  flex-direction: column;
}

.auth-showcase__slide {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.auth-slide-enter-active,
.auth-slide-leave-active {
  transition:
    opacity 350ms var(--ln-ease),
    transform 350ms var(--ln-ease);
}

.auth-slide-enter,
.auth-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  .auth-slide-enter-active,
  .auth-slide-leave-active {
    transition: none;
  }
}

.auth-showcase__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 172px;
}

.auth-showcase__slide-title {
  margin: 0;
  font-family: sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
}

.auth-showcase__slide-body {
  margin: 0;
  max-width: 42ch;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.55);
}

.auth-showcase__dots {
  display: flex;
  gap: 6px;
  margin-top: 18px;
}

.auth-showcase__dot {
  width: 6px;
  height: 6px;
  padding: 0;
  border: 0;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition:
    width 250ms var(--ln-ease),
    background-color 250ms var(--ln-ease);
}

.auth-showcase__dot:hover {
  background: rgba(255, 255, 255, 0.4);
}

.auth-showcase__dot--active {
  width: 18px;
  background: rgba(64, 156, 255, 0.9);
}

/* ---------- shared card system (the "custom domains" styling, reused everywhere) --- */

.device-card {
  position: relative;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  box-shadow:
    0 20px 40px -14px rgba(0, 0, 0, 0.55),
    0 6px 14px rgba(0, 0, 0, 0.3);
  /* fades the bottom edge toward transparent, same treatment everywhere */
  mask-image: linear-gradient(to bottom, black 0%, black 65%, transparent 98%);
  -webkit-mask-image: linear-gradient(
    to bottom,
    black 0%,
    black 65%,
    transparent 98%
  );
}

.device-card__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  border-bottom: 1px solid #eeeeec;
  font-family: var(--ln-mono);
  font-size: 12.5px;
  font-weight: 500;
  color: #111111;
}

/* ---------- card: real screenshot (conversion / landing / analytics) --------------- */

.device-card--screenshot {
  width: 264px;
  height: 172px;
}

.device-card__screenshot {
  display: block;
  width: 100%;
  height: 100%;
  /* cropped close-up rather than the full flat screenshot */
  object-fit: cover;
  object-position: center top;
}

/* ---------- card: QR code ---------------------------------------------------------- */

.device-card--qr {
  width: 176px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.device-card--qr .device-card__head {
  width: 100%;
}

.device-card__qr-img {
  width: 112px;
  height: 112px;
  margin: 16px 0 4px;
  object-fit: contain;
}

.device-card__caption {
  margin: 0 0 14px;
  font-family: var(--ln-mono);
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #918f8a;
}

/* ---------- card: domain + endless paths -------------------------------------------- */

.device-card--domains {
  width: 232px;
  height: 172px;
  padding-bottom: 16px;
}

.device-card__list {
  margin: 0;
  padding: 10px 16px 0;
  list-style: none;
}

.device-card__list li {
  padding: 5px 0;
  font-family: var(--ln-mono);
  font-size: 11.5px;
  color: #918f8a;
}

/* ---------- trust line ----------------------------------------------------- */

.auth-showcase__trust {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-family: sans-serif;
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.55);
}

.auth-showcase__trust .v-icon {
  color: rgba(255, 255, 255, 0.55);
}

@media (min-width: 960px) {
  .auth-showcase {
    display: block;
  }
}

@media (min-width: 1200px) {
  .auth-showcase__inner {
    padding: 64px 64px;
  }

  .auth-showcase__heading {
    font-size: 34px;
  }
}
</style>
